import React from "react";
import { Link } from "react-router-dom";
import { Heart, Eye, Sparkles, Users, ArrowRight, ShieldCheck, Building2, HeartHandshake, Stethoscope } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useRegisterModal } from "@/components/RegisterModal";

const Pillar = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
    <span className="w-12 h-12 rounded-full bg-orange/15 text-orange flex items-center justify-center">
      <Icon size={22} />
    </span>
    <h3 className="font-display text-xl text-teal-deep mt-4 leading-tight">{title}</h3>
    <p className="text-teal-soft text-sm mt-2 leading-relaxed">{children}</p>
  </div>
);

const Stat = ({ num, label }) => (
  <div>
    <div className="font-display text-4xl md:text-5xl text-orange leading-none">{num}</div>
    <div className="text-xs uppercase tracking-wider font-bold text-teal-soft mt-2">{label}</div>
  </div>
);

const About = () => {
  const { t } = useI18n();
  const a = t.about;
  const { open: openRegister } = useRegisterModal();
  return (
    <section className="bg-cream">
      {/* Hero */}
      <div className="relative overflow-hidden bg-teal-deep text-white py-20 md:py-28">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange/15 blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-teal/30 blur-3xl"></div>
        <div className="container-tight relative">
          <p className="eyebrow text-orange">{t.nav.about}</p>
          <h1 className="font-display text-4xl md:text-6xl leading-tight mt-3 max-w-3xl">
            {a.heroTitle1}<span className="text-orange">{a.heroTitleAccent}</span>{a.heroTitle2}
          </h1>
          <p className="text-white/80 mt-6 text-lg max-w-2xl">{a.heroBody}</p>
        </div>
      </div>

      {/* Mission */}
      <div className="container-tight py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-orange">{a.missionEyebrow}</p>
            <h2 className="font-display text-3xl md:text-4xl text-teal-deep mt-3 leading-tight">
              {a.missionTitle}
            </h2>
            <p className="text-teal-soft mt-5 leading-relaxed">{a.missionP1}</p>
            <p className="text-teal-soft mt-3 leading-relaxed">{a.missionP2}</p>
          </div>
          <div className="bg-gradient-to-br from-teal to-teal-deep rounded-3xl p-10 text-white relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-orange/20"></div>
            <Heart size={32} className="text-orange relative" />
            <blockquote className="relative font-display text-xl md:text-2xl mt-5 leading-snug italic">
              "{a.missionQuote}"
            </blockquote>
            <p className="relative text-white/70 text-sm mt-5 uppercase tracking-wider font-bold">{a.missionQuoteBy}</p>
          </div>
        </div>
      </div>

      {/* Vision */}
      <div className="bg-white py-16 md:py-20 border-y border-gray-100">
        <div className="container-tight">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1 bg-orange text-white rounded-3xl p-10 relative overflow-hidden">
              <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-white/10"></div>
              <Eye size={32} className="text-white relative" />
              <blockquote className="relative font-display text-xl md:text-2xl mt-5 leading-snug">
                {a.visionTitle}
              </blockquote>
            </div>
            <div className="order-1 md:order-2">
              <p className="eyebrow text-orange">{a.visionEyebrow}</p>
              <h2 className="font-display text-3xl md:text-4xl text-teal-deep mt-3 leading-tight">
                {a.visionEyebrow}
              </h2>
              <p className="text-teal-soft mt-5 leading-relaxed">{a.visionP1}</p>
              <p className="text-teal-soft mt-3 leading-relaxed">{a.visionP2}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pillars */}
      <div className="bg-cream py-16 md:py-20">
        <div className="container-tight">
          <div className="text-center max-w-2xl mx-auto">
            <p className="eyebrow text-orange">{a.whatWeDoEyebrow}</p>
            <h2 className="font-display text-3xl md:text-4xl text-teal-deep mt-3 leading-tight">
              {a.whatWeDoTitle}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Pillar icon={Users} title={a.forUsersTitle}>{a.forUsersBody}</Pillar>
            <Pillar icon={Sparkles} title={a.forEntrepreneursTitle}>{a.forEntrepreneursBody}</Pillar>
            <Pillar icon={Stethoscope} title={a.forLeadersTitle}>{a.forLeadersBody}</Pillar>
            <Pillar icon={Building2} title={a.forEntitiesTitle}>{a.forEntitiesBody}</Pillar>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="container-tight py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="bg-white rounded-3xl border border-gray-200 p-8">
            <span className="w-12 h-12 rounded-full bg-teal text-white flex items-center justify-center">
              <HeartHandshake size={22} />
            </span>
            <h3 className="font-display text-2xl text-teal-deep mt-4">{a.valueSolidarityTitle}</h3>
            <p className="text-teal-soft mt-2 leading-relaxed">{a.valueSolidarityBody}</p>
          </div>
          <div className="bg-white rounded-3xl border border-gray-200 p-8">
            <span className="w-12 h-12 rounded-full bg-orange text-white flex items-center justify-center">
              <ShieldCheck size={22} />
            </span>
            <h3 className="font-display text-2xl text-teal-deep mt-4">{a.valueTrustTitle}</h3>
            <p className="text-teal-soft mt-2 leading-relaxed">{a.valueTrustBody}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white py-14 border-y border-gray-100">
        <div className="container-tight grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <Stat num="100%" label={a.statFree} />
          <Stat num="2" label={a.statLanguages} />
          <Stat num="13" label={a.statEntityTypes} />
          <Stat num="∞" label={a.statPossibilities} />
        </div>
      </div>

      {/* CTA */}
      <div className="container-tight py-16 md:py-20 text-center">
        <p className="eyebrow text-orange">{a.ctaEyebrow}</p>
        <h2 className="font-display text-3xl md:text-4xl text-teal-deep mt-3 max-w-2xl mx-auto leading-tight">
          {a.ctaTitle1}<span className="text-orange italic">{a.ctaTitleAccent}</span>{a.ctaTitle2}
        </h2>
        <p className="text-teal-soft mt-4 max-w-xl mx-auto">{a.ctaBody}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={openRegister} className="btn-orange" data-testid="about-cta-register">
            {a.ctaRegister} <ArrowRight size={14} />
          </button>
          <Link to="/directory" className="btn-teal-outline" data-testid="about-cta-directory">
            {a.ctaDirectory}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default About;
